import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Gallery.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ChevronRight, Search } from 'lucide-react';
import SEO from '../SEO';
const API_URL = 'https://api.sohanihandicrafts.com:5010';

// --- Reusable Breadcrumb ---
const ArtisanBreadcrumb = ({ title = "Infrastructure", bgImage, paths = [] }) => {
  const breadcrumbs = paths.length > 0 ? paths : [
    { name: "Home", link: "/" },
    { name: "Infrastructure", link: "/gallery" }
  ];

  return (
  
    
    <div className="bc-art-wrapper">
      <div className="bc-art-bg" style={{ backgroundImage: `url(${bgImage || ''})` }}>
        <div className="bc-art-overlay"></div>
      </div>
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 80, repeat: Infinity, ease: "linear" }} className="bc-art-deco-ring">
        <svg viewBox="0 0 100 100">
           <path d="M50 0C22.4 0 0 22.4 0 50s22.4 50 50 50 50-22.4 50-50S77.6 0 50 0zm0 90C27.9 90 10 72.1 10 50S27.9 10 50 10s40 17.9 40 40-17.9 40-40 40z" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3" strokeDasharray="4 4"/>
        </svg>
      </motion.div>
      <div className="bc-art-content">
        <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bc-art-title">{title}</motion.h1>
        <motion.nav initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bc-art-bar">
          <ul className="bc-art-list">
            <li><a href="/" className="bc-art-home-icon"><Home size={16} /></a></li>
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="bc-art-item">
                <span className="bc-art-sep"><ChevronRight size={12} strokeWidth={3} /></span>
                {index === breadcrumbs.length - 1 ? <span className="bc-art-text active">{crumb.name}</span> : <a href={crumb.link} className="bc-art-text link">{crumb.name}</a>}
              </li>
            ))}
          </ul>
        </motion.nav>
      </div>
    </div>
  
  );
};

// --- Hook for Scroll Animations ---
const useAnimateOnScroll = (options) => {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('gallery-is-visible');
        observer.unobserve(entry.target);
      }
    }, options);
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);
  return ref;
};

// --- Main Gallery Component ---
export default function SuperbGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // FETCH DATA FROM BACKEND
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/gallery/all`);
        setImages(res.data);
      } catch (err) {
        console.error("Failed to fetch gallery", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const handleNext = () => setSelectedImageIndex((prev) => (prev + 1) % images.length);
  const handlePrev = () => setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);

  if (loading) return <div className="gallery-loader">Loading Infrastructure ...</div>;

  return (
     <>
      
    <SEO 
  title="Our Infrastructure & Artisan Workshop Gallery"
  description="Step inside our state-of-the-art manufacturing unit in Jodhpur. See our master artisans at work and the advanced infrastructure behind our high-quality handicrafts."
  keywords="handicraft factory jodhpur, furniture manufacturing process, wood seasoning plant india, artisan workshop photos, furniture production unit, jodhpur industry gallery"
  url="/gallery"
/>
      <ArtisanBreadcrumb title="Infrastructure" />
      <div className="superb-gallery-page">
        <div className="superb-gallery-grid">
          {images.map((image, index) => (
            <GalleryItem 
              key={image._id} 
              image={image} 
              onClick={() => setSelectedImageIndex(index)} 
            />
          ))}
        </div>

        <AnimatePresence>
          {selectedImageIndex !== null && (
            <Lightbox
              image={images[selectedImageIndex]}
              onClose={() => setSelectedImageIndex(null)}
              onNext={handleNext}
              onPrev={handlePrev}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

const GalleryItem = ({ image, onClick }) => {
  const itemRef = useAnimateOnScroll({ threshold: 0.1 });
  // Map backend layout to CSS classes
  const layoutClass = `gallery-item--${image.layout || 'regular'}`;

  return (
    <motion.div 
      ref={itemRef} 
      className={`gallery-item ${layoutClass}`} 
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <img src={`${API_URL}/${image.image}`} alt={image.alt} className="gallery-item-img" />
      <div className="gallery-item-overlay">
        <Search className="gallery-item-icon" size={32} />
      </div>
    </motion.div>
  );
};

const Lightbox = ({ image, onClose, onNext, onPrev }) => {
  return (
    <motion.div 
      className="gallery-lightbox" 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button className="gallery-lightbox-btn gallery-lightbox-close" onClick={onClose}>&times;</button>
      <button className="gallery-lightbox-btn gallery-lightbox-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }}>&lt;</button>
      <button className="gallery-lightbox-btn gallery-lightbox-next" onClick={(e) => { e.stopPropagation(); onNext(); }}>&gt;</button>
      <div className="gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>
        <img src={`${API_URL}/${image.image}`} alt={image.alt} className="gallery-lightbox-img" />
        <div className="lightbox-caption">{image.alt}</div>
      </div>
    </motion.div>
    
  );
};